/* eslint-disable react/prop-types */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';
import * as Constants from '../../Constant';

import { useLocalStorage } from 'react-use';

import {
  EditingState,
  PagingState,
  IntegratedPaging,
  SortingState,
  IntegratedSorting,
  SearchState,
  IntegratedFiltering,
  SelectionState,
  IntegratedSelection,
  DataTypeProvider
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  PagingPanel,
  TableColumnResizing,
  DragDropProvider,
  TableColumnReordering,
  SearchPanel,
  Toolbar,
  TableSelection,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@mui/material/Paper';

import { CUSTOMER_INLINE_COLUMNS } from './helper';
import toast from 'react-hot-toast';
import { CustomerFocusableCell, CustomerLookupProvider } from './CustomerTableInlineEditing';
import { hasCustomerValidationErrors, validateCustomerChanges } from './helper';
import { Popup, PopupEditing } from './CustomerPopupEditing';
import HeaderFilterCell, { getRowCellValue, HeaderFilterContext } from './customTemplate/HeaderFilterCell';
import axios from 'axios';

import CustomToolbarRoot from '../../UserNew/table/customTemplate/CustomToolbarRoot';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';

/* eslint-disable no-shadow */

const QBOActiveStatusProvider = (props) => {
  return <DataTypeProvider formatterComponent={QBOActiveStatusFormatter} {...props} />;
};

const CustomerStatusProvider = (props) => {
  return <DataTypeProvider formatterComponent={CustomerStatusFormatter} {...props} />;
};

const QBOActiveStatusFormatter = ({ value }) => {
  const isActive = value === true;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          backgroundColor: isActive ? 'green' : 'red'
        }}
      ></span>
      <span>{isActive ? 'Active' : 'InActive'}</span>
    </div>
  );
};

const CustomerStatusFormatter = ({ value }) => {
  // Support both raw API booleans and rows formatted before the boolean editor was introduced.
  const isActive = value === true || value === 'Enabled';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          backgroundColor: isActive ? 'green' : 'red'
        }}
      ></span>
      <span>{isActive ? 'Enabled' : 'Disabled'}</span>
    </div>
  );
};

const getRowId = (row) => row.id;

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

// A single click is left to TableInlineCellEditing. Double-clicking a data row
// switches from inline editing to the full popup editor.
const ClickableCustomerRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.DisplayName || 'customer'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

export default function CustomerTable({ rows, setRows, selection, setSelection }) {
  const [customerTabIndex, setCustomerTabIndex] = useState(0);

  const [columns] = useState([
    { name: 'DisplayName', title: 'CustomerName' },
    { name: 'Email', title: 'Email' },
    { name: 'Phone', title: 'Phone' },
    { name: 'customerStatus', title: 'User Active' },
    { name: 'id', title: 'CustomerId' },

    { name: 'GivenName', title: 'GivenName' },
    { name: 'MiddleName', title: 'MiddleName' },
    { name: 'FamilyName', title: 'FamilyName' },
    { name: 'CompanyName', title: 'CompanyName' },
    { name: 'PrintOnCheckName', title: 'PrintOnCheckName' },

    { name: 'Active', title: 'QBO Active' },
    { name: 'Title', title: 'Title' },
    { name: 'Suffix', title: 'Suffix' },
    { name: 'Taxable', title: 'Taxable' },
    { name: 'SyncToken', title: 'SyncToken' },

    { name: 'Line1', title: 'Line1' },
    { name: 'City', title: 'City' },
    { name: 'CountrySubDivisionCode', title: 'CountrySubDivisionCode' },
    { name: 'PostalCode', title: 'PostalCode' },

    { name: 'Job', title: 'Job' },
    { name: 'ParentRef', title: 'ParentRef' },
    { name: 'BillWithParent', title: 'BillWithParent' },
    { name: 'PreferredDeliveryMethod', title: 'PreferredDeliveryMethod' },
    { name: 'BalanceWithJobs', title: 'BalanceWithJobs' },
    { name: 'IsProject', title: 'IsProject' }
  ]);

  //it was using useLocalStorage customersColumnWidths, update it once customer columns are done
  const [customerColumnWidths, setCustomerColumnWidths] = useState([
    { columnName: 'DisplayName', width: 180 },
    { columnName: 'Email', width: 180 },
    { columnName: 'Phone', width: 180 },
    { columnName: 'customerStatus', width: 180 },
    { columnName: 'id', width: 180 },

    { columnName: 'GivenName', width: 180 },
    { columnName: 'MiddleName', width: 180 },
    { columnName: 'FamilyName', width: 180 },
    { columnName: 'CompanyName', width: 180 },
    { columnName: 'PrintOnCheckName', width: 180 },

    { columnName: 'Active', width: 180 },
    { columnName: 'Title', width: 180 },
    { columnName: 'Suffix', width: 180 },
    { columnName: 'Taxable', width: 180 },
    { columnName: 'SyncToken', width: 180 },

    { columnName: 'Line1', width: 180 },
    { columnName: 'City', width: 180 },
    { columnName: 'CountrySubDivisionCode', width: 180 },
    { columnName: 'PostalCode', width: 180 },

    { columnName: 'Job', width: 180 },
    { columnName: 'ParentRef', width: 180 },
    { columnName: 'BillWithParent', width: 180 },
    { columnName: 'PreferredDeliveryMethod', width: 180 },
    { columnName: 'BalanceWithJobs', width: 180 },
    { columnName: 'IsProject', width: 180 }
  ]);
  //it was using useLocalStorage customersColumnOrder, update it once customer columns are done
  const [customerColumnOrder, setCustomerColumnOrder] = useState([
    'DisplayName',
    'Email',
    'Phone',
    'customerStatus',
    'id',

    'GivenName',
    'MiddleName',
    'FamilyName',
    'CompanyName',
    'PrintOnCheckName',

    'Active',
    'Title',
    'Suffix',
    'Taxable',
    'SyncToken',

    'Line1',
    'City',
    'CountrySubDivisionCode',
    'PostalCode',

    'Job',
    'ParentRef',
    'BillWithParent',
    'PreferredDeliveryMethod',
    'BalanceWithJobs',
    'IsProject'
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'DisplayName', direction: 'asc' }]);
  const [hiddenColumnNames, setHiddenColumnNames] = useState(['']);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizes] = useState([25, 50, 100]);
  const [searchValue, setSearchState] = useState('');

  const [customerStatusColumn] = useState(['customerStatus']);
  const [quickbookActiveCustomer] = useState(['Active']);
  const [columnFilters, setColumnFilters] = useLocalStorage('customersColumnFilters', {});

  const [lookupColumns] = useState(['customerStatus', 'Active', 'Title']);

  //to differentiate between popup and inline editing
  const [, setEditingMode] = useState(null);
  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [validationStatus, setValidationStatus] = useState({});

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
    setEditingMode(mode);
  }, []);

  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    changeEditingMode('popup');
  }, [changeEditingMode]);

  const openCustomerDetails = useCallback(
    (rowId) => {
      setEditingCellIds([]);
      changeEditingMode('popup');
      setEditingRowIds([rowId]);
    },
    [changeEditingMode]
  );

  const editingStateColumnExtensions = useMemo(
    () => columns.map(({ name }) => ({ columnName: name, editingEnabled: CUSTOMER_INLINE_COLUMNS.includes(name) })),
    [columns]
  );

  const commitChanges = ({ changed }) => {
    let changedRows;
    const validChanges = Object.fromEntries(
      Object.entries(changed || {}).filter(
        ([, rowChanges]) => rowChanges && typeof rowChanges === 'object' && Object.keys(rowChanges).length > 0
      )
    );

    if (Object.keys(validChanges).length > 0) {
      const newValidationStatus = validateCustomerChanges(validChanges);
      setValidationStatus(newValidationStatus);

      if (hasCustomerValidationErrors(validChanges, newValidationStatus)) {
        toast.error('Required field cannot be empty.');
        return; // stop: no row merge, no backend call
      }

      changedRows = rows.map((row) => (validChanges[row.id] ? { ...row, ...validChanges[row.id] } : row));

      // Popup mode is active iff a row is currently open in the popup editor.
      // Anything else that reaches commitChanges with `changed` is inline.
      const isPopupCommit = editingRowIds.length > 0;

      if (!isPopupCommit) {
        const updates = Object.keys(validChanges).map((rowId) => {
          const updatedRow = changedRows.find((row) => String(row.id) === String(rowId));

          if (!updatedRow) {
            throw new Error(`Customer row ${rowId} was not found`);
          }

          const customerPayload = {
            // eslint-disable-next-line no-undef
            token: localStorage.getItem('token'),
            customerId: updatedRow.id,
            changes: validChanges[rowId]
          };

          const url = Constants.BASE_URL + 'client/updateCustomerFields';
          return axios.put(url, customerPayload);

          // return clientService.updateCustomerFields(updatedRow.id, validChanges[rowId]);
        });

        // eslint-disable-next-line no-undef
        Promise.all(updates)
          .then(() => toast.success('Successfully updated customer'))
          .catch((error) => {
            setRows(rows);
            const steps = error?.response?.data?.steps || [];
            const failedStep = steps.find((step) => step.status === 'failed');
            const message = failedStep
              ? `${failedStep.name}: ${failedStep.message}`
              : error?.response?.data?.message || error?.message || 'Could not update customer';
            return toast.error(`Could not fully update customer. ${message}`);
          });
      }
    }
    // Only update rows if something actually changed
    if (changedRows !== undefined) {
      setRows(changedRows);
    }
  };

  const onUpdateRow = useCallback(
    (updatedRow) => {
      setRows((prevRows) => prevRows.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)));
    },
    [setRows]
  );

  const exporterRef = useRef(null);

  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Column header filters
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  return (
    <div>
      <span>Total rows selected: {selection.length}</span>
      <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
        <Paper>
          <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
            <EditingState
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(cellIds) => {
                if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                  const editableCellIds = cellIds.filter(({ columnName }) =>
                    CUSTOMER_INLINE_COLUMNS.includes(columnName)
                  );
                  changeEditingMode(editableCellIds.length ? 'inline' : null);
                  setEditingCellIds(editableCellIds);
                }
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(rowIds) => {
                setEditingRowIds(rowIds);
                if (rowIds.length) {
                  setEditingCellIds([]);
                  changeEditingMode('popup');
                } else if (editingModeRef.current === 'popup') {
                  changeEditingMode(null);
                }
              }}
              onCommitChanges={commitChanges}
              columnExtensions={editingStateColumnExtensions}
            />

            <CustomerLookupProvider for={lookupColumns} />

            <CustomerStatusProvider for={customerStatusColumn} />
            <QBOActiveStatusProvider for={quickbookActiveCustomer} />

            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SelectionState selection={selection} onSelectionChange={setSelection} />
            <IntegratedSelection />

            <DragDropProvider />

            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting />

            <SearchState value={searchValue} onValueChange={setSearchState} />
            <IntegratedFiltering />
            <IntegratedPaging />

            <Table
              rowComponent={(props) => <ClickableCustomerRow {...props} onOpen={openCustomerDetails} />}
              cellComponent={(props) => (
                <CustomerFocusableCell
                  {...props}
                  validationStatus={validationStatus}
                  setValidationStatus={setValidationStatus}
                />
              )}
              containerComponent={ResponsiveTableContainer}
              headComponent={StickyTableHead}
            />
            <TableColumnResizing columnWidths={customerColumnWidths} onColumnWidthsChange={setCustomerColumnWidths} />
            <TableColumnReordering order={customerColumnOrder} onOrderChange={setCustomerColumnOrder} />

            {/* customer header filter cell */}
            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />

            <TableSelection showSelectAll />
            <PagingPanel pageSizes={pageSizes} />

            {/* Native grid edit commands, displayed through the shared KebabCommandCell. */}
            <TableEditColumn
              width={90}
              showEditCommand
              cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
            />

            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

            <PopupEditing
              popupComponent={Popup}
              customerTabIndex={customerTabIndex}
              setCustomerTabIndex={setCustomerTabIndex}
              onUpdateRow={onUpdateRow}
            />
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              onHiddenColumnNamesChange={setHiddenColumnNames}
            />
            {/* Pass the custom root to the Toolbar */}
            <Toolbar rootComponent={CustomToolbarRoot} />
            <SearchPanel />
            <ColumnChooser />
            <ExportPanel startExport={startExport} />
          </Grid>
          <GridExporter
            ref={exporterRef}
            columns={columns}
            rows={filteredRows}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
            hiddenColumnNames={hiddenColumnNames}
          />
        </Paper>
      </HeaderFilterContext.Provider>
    </div>
  );
}
